import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  Eye,
  Filter,
  Info,
  Search,
  ShieldCheck,
  Upload,
  X,
} from 'lucide-react';
import UniversalLayout from './components/UniversalLayout';
import { META_AD_BRANDS, META_AD_CATEGORIES, categoryById } from './metaAdsBrandData';
import {
  META_ADS_SNAPSHOT_STORAGE_KEY,
  buildMetaAdsSnapshotTemplate,
  isFreshObservation,
  parseMetaAdsSnapshotCsv,
} from './metaAdsPublicSnapshot';
import './MetaAdsDashboardPage.css';

const MARKETS = [
  { id: 'US', label: 'United States' },
  { id: 'GB', label: 'United Kingdom' },
  { id: 'DE', label: 'Germany' },
  { id: 'FR', label: 'France' },
];

const SORTS = [
  { id: 'curatedRank', label: 'Curated shortlist' },
  { id: 'activeAds', label: 'Most active ads' },
  { id: 'newAds', label: 'Most new creatives' },
  { id: 'averageDays', label: 'Longest running' },
  { id: 'checkedAt', label: 'Recently checked' },
];

function initials(name) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();
}

function metaUrlForMarket(brand, market) {
  const url = new URL(brand.metaUrl);
  url.searchParams.set('country', market);
  return url.toString();
}

function formatCheckedAt(value) {
  if (!value) return 'Not checked';
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(timestamp);
}

function BrandMark({ brand, large = false }) {
  const [imageFailed, setImageFailed] = useState(false);
  return (
    <span
      className={`meta-brand-mark${large ? ' meta-brand-mark--large' : ''}`}
      style={{ '--brand-accent': brand.accent }}
      aria-hidden="true"
    >
      {brand.logo && !imageFailed ? (
        <img src={brand.logo} alt="" onError={() => setImageFailed(true)} />
      ) : (
        initials(brand.name)
      )}
    </span>
  );
}

function Metric({ label, value, detail, Icon }) {
  return (
    <div className="meta-summary-item">
      <div className="meta-summary-icon"><Icon size={17} /></div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{detail}</small>
      </div>
    </div>
  );
}

function ImportSnapshotModal({ csv, errors, onCsvChange, onClose, onImport, onDownload }) {
  return (
    <div className="meta-drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="meta-import-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Import public Meta Ad Library snapshot"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="meta-drawer-close" onClick={onClose} aria-label="Close import">
          <X size={18} />
        </button>
        <span className="meta-eyebrow">No-key refresh</span>
        <h2>Import public observations</h2>
        <p>
          Open each brand in Meta’s public library, select the advertiser when possible, and record the visible totals.
          Paste the completed CSV here; observations stay in this browser.
        </p>
        <div className="meta-import-actions">
          <button type="button" onClick={onDownload}><Download size={15} /> Download template</button>
          <code>brand, active_ads, new_30d, avg_runtime_days, checked_at, source_url</code>
        </div>
        <textarea
          value={csv}
          onChange={(event) => onCsvChange(event.target.value)}
          placeholder={'brand,active_ads,new_30d,avg_runtime_days,checked_at,source_url\nSKIMS,340,92,41,2026-08-02,https://www.facebook.com/ads/library/...'}
          aria-label="Public snapshot CSV"
        />
        {!!errors.length && (
          <div className="meta-import-errors" role="alert">
            {errors.slice(0, 4).map((error) => <span key={error}>{error}</span>)}
          </div>
        )}
        <div className="meta-import-footer">
          <button type="button" className="meta-secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="meta-primary-button" onClick={onImport}><Upload size={15} /> Import snapshot</button>
        </div>
      </section>
    </div>
  );
}

function BrandDrawer({ brand, market, onClose }) {
  if (!brand) return null;
  const category = categoryById[brand.category];
  const sourceUrl = brand.sourceUrl || metaUrlForMarket(brand, market);
  return (
    <div className="meta-drawer-backdrop" role="presentation" onMouseDown={onClose}>
      <aside
        className="meta-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`${brand.name} public ad activity`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="meta-drawer-close" onClick={onClose} aria-label="Close details">
          <X size={18} />
        </button>

        <header className="meta-drawer-head">
          <BrandMark brand={brand} large />
          <div>
            <span className="meta-eyebrow">{category.label}</span>
            <h2>{brand.name}</h2>
            <p>Curated rank #{brand.curatedRank} · public-library workflow</p>
          </div>
        </header>

        {brand.observed ? (
          <>
            <div className="meta-observation-status is-verified">
              <CheckCircle2 size={16} />
              <div><strong>Public observation saved</strong><span>Checked {formatCheckedAt(brand.checkedAt)}</span></div>
            </div>
            <dl className="meta-drawer-stats">
              <div><dt>Active ads</dt><dd>{brand.activeAds.toLocaleString()}</dd></div>
              <div><dt>New in 30 days</dt><dd>{brand.newAds ?? '—'}</dd></div>
              <div><dt>Average runtime</dt><dd>{brand.averageDays == null ? '—' : `${brand.averageDays}d`}</dd></div>
              <div><dt>Freshness</dt><dd>{isFreshObservation(brand.checkedAt) ? 'Fresh' : 'Refresh'}</dd></div>
            </dl>
          </>
        ) : (
          <div className="meta-observation-status">
            <Info size={16} />
            <div><strong>No public check saved yet</strong><span>Open Meta, verify the advertiser, then import an observation.</span></div>
          </div>
        )}

        <section className="meta-drawer-section">
          <h3>What to inspect</h3>
          <div className="meta-angle-list">
            {brand.angles.map((angle, index) => (
              <span key={angle}><b>{String(index + 1).padStart(2, '0')}</b>{angle}</span>
            ))}
          </div>
        </section>

        <a className="meta-library-link" href={sourceUrl} target="_blank" rel="noopener noreferrer">
          Open public Meta search <ExternalLink size={15} />
        </a>
        <p className="meta-link-caveat">Meta may begin with keyword matches. Select the official advertiser to avoid counting unrelated ads.</p>
      </aside>
    </div>
  );
}

function loadSavedObservations() {
  try {
    return JSON.parse(window.localStorage.getItem(META_ADS_SNAPSHOT_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

function MetaAdsDashboardContent() {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('curatedRank');
  const [market, setMarket] = useState('US');
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [observations, setObservations] = useState(loadSavedObservations);
  const [importOpen, setImportOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [importErrors, setImportErrors] = useState([]);

  useEffect(() => {
    window.localStorage.setItem(META_ADS_SNAPSHOT_STORAGE_KEY, JSON.stringify(observations));
  }, [observations]);

  const enrichedBrands = useMemo(() => META_AD_BRANDS.map((brand) => ({
    ...brand,
    ...(observations[brand.id] || {}),
    observed: Boolean(observations[brand.id]),
  })), [observations]);

  const displayed = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return enrichedBrands
      .filter((brand) => category === 'all' || brand.category === category)
      .filter((brand) => !normalizedQuery || brand.name.toLowerCase().includes(normalizedQuery))
      .sort((a, b) => {
        if (sort === 'curatedRank') {
          return a.curatedRank - b.curatedRank
            || categoryById[a.category].label.localeCompare(categoryById[b.category].label)
            || a.name.localeCompare(b.name);
        }
        if (a.observed !== b.observed) return a.observed ? -1 : 1;
        if (sort === 'checkedAt') return String(b.checkedAt || '').localeCompare(String(a.checkedAt || ''));
        return (b[sort] ?? -1) - (a[sort] ?? -1) || a.name.localeCompare(b.name);
      });
  }, [category, enrichedBrands, query, sort]);

  const summary = useMemo(() => {
    const verified = displayed.filter((brand) => brand.observed);
    return {
      verified: verified.length,
      fresh: verified.filter((brand) => isFreshObservation(brand.checkedAt)).length,
      activeAds: verified.reduce((total, brand) => total + brand.activeAds, 0),
      coverage: displayed.length ? Math.round((verified.length / displayed.length) * 100) : 0,
    };
  }, [displayed]);

  const selectedBrand = enrichedBrands.find((brand) => brand.id === selectedBrandId) || null;

  const resetFilters = () => {
    setCategory('all');
    setQuery('');
    setSort('curatedRank');
  };

  const downloadTemplate = () => {
    const blob = new Blob([buildMetaAdsSnapshotTemplate(META_AD_BRANDS)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'meta-ads-public-snapshot.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const importSnapshot = () => {
    const result = parseMetaAdsSnapshotCsv(csv, META_AD_BRANDS);
    setImportErrors(result.errors);
    if (!Object.keys(result.records).length) return;
    setObservations((current) => ({ ...current, ...result.records }));
    setCsv('');
    setImportOpen(false);
  };

  return (
    <main className="meta-dashboard">
      <header className="meta-dashboard-header">
        <div>
          <span className="meta-eyebrow">Creative intelligence</span>
          <h1>Meta Ads leaderboard</h1>
          <p>Track category leaders with public Ad Library checks—no API approval or access token required.</p>
        </div>
        <div className="meta-source-status is-public">
          <span><i /> Public Library mode</span>
          <small>100 brands · local observations</small>
        </div>
      </header>

      <section className="meta-public-workflow">
        <div className="meta-public-workflow-icon"><ShieldCheck size={19} /></div>
        <div>
          <strong>No-key workflow</strong>
          <p>Open the source search, select the official advertiser, then import the visible totals. Rankings update from your saved observations.</p>
        </div>
        <div className="meta-public-workflow-actions">
          <button type="button" className="meta-secondary-button" onClick={downloadTemplate}><Download size={14} /> CSV template</button>
          <button type="button" className="meta-primary-button" onClick={() => { setImportErrors([]); setImportOpen(true); }}><Upload size={14} /> Import snapshot</button>
        </div>
      </section>

      <section className="meta-category-strip" aria-label="Brand categories">
        <button type="button" className={category === 'all' ? 'is-active' : ''} onClick={() => setCategory('all')}>
          <span>All categories</span><b>{META_AD_BRANDS.length}</b>
        </button>
        {META_AD_CATEGORIES.map((item) => (
          <button
            type="button"
            key={item.id}
            className={category === item.id ? 'is-active' : ''}
            onClick={() => setCategory(item.id)}
            style={{ '--category-accent': item.accent }}
          >
            <i /><span>{item.label}</span><b>10</b>
          </button>
        ))}
      </section>

      <section className="meta-summary" aria-label="Public data coverage">
        <Metric Icon={BarChart3} label="Brands tracked" value={displayed.length.toLocaleString()} detail="Curated category leaders" />
        <Metric Icon={CheckCircle2} label="Public checks" value={summary.verified.toLocaleString()} detail={`${summary.fresh} fresh in 14 days`} />
        <Metric Icon={ShieldCheck} label="Ads observed" value={summary.verified ? summary.activeAds.toLocaleString() : '—'} detail="From imported public checks" />
        <Metric Icon={Upload} label="Coverage" value={`${summary.coverage}%`} detail="No access token needed" />
      </section>

      <section className="meta-leaderboard">
        <div className="meta-toolbar">
          <div className="meta-search-wrap">
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search 100 brands" aria-label="Search brands" />
            {query && <button type="button" onClick={() => setQuery('')} aria-label="Clear search"><X size={14} /></button>}
          </div>
          <div className="meta-toolbar-controls">
            <label>
              <span>Market</span>
              <select value={market} onChange={(event) => setMarket(event.target.value)}>
                {MARKETS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
              <ChevronDown size={14} />
            </label>
            <label>
              <span>Rank by</span>
              <select value={sort} onChange={(event) => setSort(event.target.value)}>
                {SORTS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
              </select>
              <ChevronDown size={14} />
            </label>
          </div>
        </div>

        <div className="meta-table-meta">
          <div><strong>{displayed.length} brands</strong><span>Public checks for {MARKETS.find((item) => item.id === market)?.label}</span></div>
          <button type="button" className="meta-methodology" title="Curated order is the fallback. Imported public observations take priority for metric-based sorts.">
            <Info size={14} /> Methodology
          </button>
        </div>

        <div className="meta-table-wrap">
          <table className="meta-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Brand</th>
                <th>Category</th>
                <th>Active ads</th>
                <th>New / 30d</th>
                <th>Avg. runtime</th>
                <th>Last checked</th>
                <th>Evidence</th>
                <th><span className="sr-only">View</span></th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((brand, index) => (
                <tr key={brand.id} onClick={() => setSelectedBrandId(brand.id)}>
                  <td><span className={`meta-rank ${index < 3 ? 'is-top' : ''}`}>{String(index + 1).padStart(2, '0')}</span></td>
                  <td>
                    <div className="meta-brand-cell">
                      <BrandMark brand={brand} />
                      <div><strong>{brand.name}</strong><small>Curated #{brand.curatedRank} in category</small></div>
                    </div>
                  </td>
                  <td><span className="meta-category-cell"><i style={{ background: brand.accent }} />{categoryById[brand.category].label}</span></td>
                  <td><strong>{brand.observed ? brand.activeAds.toLocaleString() : '—'}</strong></td>
                  <td>{brand.observed ? (brand.newAds ?? '—') : '—'}</td>
                  <td>{brand.observed && brand.averageDays != null ? `${brand.averageDays} days` : '—'}</td>
                  <td>{brand.observed ? formatCheckedAt(brand.checkedAt) : 'Not checked'}</td>
                  <td>
                    <span className={`meta-evidence ${brand.observed ? 'is-verified' : ''}`}>
                      {brand.observed ? <CheckCircle2 size={13} /> : <Info size={13} />}
                      {brand.observed ? 'Public check' : 'Curated'}
                    </span>
                  </td>
                  <td><button type="button" onClick={() => setSelectedBrandId(brand.id)} aria-label={`View ${brand.name}`}><Eye size={16} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!displayed.length && (
          <div className="meta-empty">
            <Filter size={22} />
            <strong>No brands match these filters</strong>
            <button type="button" onClick={resetFilters}>Reset filters</button>
          </div>
        )}
      </section>

      <footer className="meta-data-note">
        <Info size={15} />
        <p><strong>Transparent by design.</strong> “Curated” rows are discovery candidates, not live measurements. “Public check” rows come from snapshots you imported after viewing Meta’s public Ad Library. Keyword results may include unrelated mentions, so advertiser-level filtering is recommended.</p>
      </footer>

      <BrandDrawer brand={selectedBrand} market={market} onClose={() => setSelectedBrandId(null)} />
      {importOpen && (
        <ImportSnapshotModal
          csv={csv}
          errors={importErrors}
          onCsvChange={setCsv}
          onClose={() => setImportOpen(false)}
          onImport={importSnapshot}
          onDownload={downloadTemplate}
        />
      )}
    </main>
  );
}

export function MetaAdsDashboardPage() {
  return (
    <UniversalLayout>
      <MetaAdsDashboardContent />
    </UniversalLayout>
  );
}

export default MetaAdsDashboardPage;
